import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { getVariantValue } from "../cells/cellVariant";
import { resolveColumnMeta } from "../columns/columnKinds";
import type {
    DataGridPin,
    DataGridResolvedColumnMeta,
    DataGridTable,
} from "../columns/types";
import type { RangeSegmentPart } from "../features/range/rangeKey";
import { getDisplayColumns } from "../focus/navigation";
import { getPinEdge } from "../layout/pinning";
import type { DataGridAtoms, DataGridReactTable } from "../types";

/** Per-column facts every row needs, computed once per column layout. */
export interface RowLayout {
    metaById: ReadonlyMap<string, DataGridResolvedColumnMeta>;
    pinEdgeById: ReadonlyMap<string, DataGridPin>;
    colIndexById: ReadonlyMap<string, number>;
    /** Columns with `variants`: the chosen variant, else the default. */
    variantById: ReadonlyMap<string, string>;
    /** Visible column ids by pin part, in display order. */
    columnIdsByPart: Readonly<Record<RangeSegmentPart, readonly string[]>>;
    /** A visible column wraps, so rows are measured instead of fixed height. */
    hasWrap: boolean;
}

const buildRowLayout = <TData extends RowData>(
    table: DataGridTable<TData>,
    variants: Readonly<Record<string, string>>,
): RowLayout => {
    const metaById = new Map<string, DataGridResolvedColumnMeta>();
    const pinEdgeById = new Map<string, DataGridPin>();
    const colIndexById = new Map<string, number>();
    const variantById = new Map<string, string>();
    let hasWrap = false;
    getDisplayColumns(table).forEach((column, index) => {
        const meta = resolveColumnMeta(column.columnDef, column.id);
        metaById.set(column.id, meta);
        colIndexById.set(column.id, index + 1);
        hasWrap ||= meta.overflow === "wrap";
        const edge = getPinEdge(column);
        if (edge) {
            pinEdgeById.set(column.id, edge);
        }
        if (meta.variants) {
            variantById.set(
                column.id,
                getVariantValue(meta.variants, variants[column.id]),
            );
        }
    });
    const columnIdsByPart = {
        start: table.getStartVisibleLeafColumns().map(({ id }) => id),
        center: table.getCenterVisibleLeafColumns().map(({ id }) => id),
        end: table.getEndVisibleLeafColumns().map(({ id }) => id),
    };
    return {
        metaById,
        pinEdgeById,
        colIndexById,
        variantById,
        columnIdsByPart,
        hasWrap,
    };
};

/**
 * The body's row layout, rebuilt when which columns show, where, or in which
 * variant changes; not on table identity. Grouping counts as where: grouped
 * columns move to the front. A variant change gives rows a new layout, so
 * only cells redraw: the column definitions stay as they are.
 */
export const useRowLayout = <TData extends RowData>({
    table,
    columnVariants,
}: {
    table: DataGridReactTable<TData>;
    columnVariants: DataGridAtoms["columnVariants"];
}) => {
    const { columnOrder, columnVisibility, columnPinning, grouping } =
        table.state;
    const variants = useSelector(columnVariants);
    return useMemoByKeys(
        () => buildRowLayout(table, variants),
        [
            columnOrder,
            columnVisibility,
            columnPinning,
            grouping,
            table.options.columns,
            variants,
        ],
    );
};
